import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  TextField,
  Button,
  Avatar,
  Autocomplete,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import UploadIcon from "@mui/icons-material/Upload";
import { IMAGE_URL } from "../../../services/api";
import ProtectedImage from "../../../components/ProtectedImage";
// Gender options
const genderOptions = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];



const BasicDetailEditForm = ({ userDetails, onCancel, onSave }) => {
  
  const originalImageRef = useRef(null);
  const imageUpdatedRef = useRef(false);

  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    gender: "",
    nationality: "",
    date_of_birth: "",
    aadhar_number: "",
    residentialAddress: "",
    currentAddress: "",
    about: "",
    profile_image: "",
  });

  const [loadingImage, setLoadingImage] = useState(false);

  useEffect(() => {
    if (userDetails) {
      const base = {
        first_name: userDetails.first_name || "",
        last_name: userDetails.last_name || "",
        email: userDetails.email || "",
        mobile_number: userDetails.mobile_number || "",
        gender: userDetails.gender || "",
        nationality: userDetails.nationality || "",
        date_of_birth: userDetails.profile_data?.date_of_birth || "",
        aadhar_number: userDetails.profile_data?.aadhar_number || "",
        residentialAddress: userDetails.profile_data?.address?.residentialAddress || "",
        currentAddress: userDetails.profile_data?.address?.currentAddress || "",
        about: userDetails.profile_data?.about || "",
        profile_image: "", // will set after base64 conversion
      };

      setInitialValues(base);

      if (userDetails.profile_data?.profile_image) {
        setLoadingImage(true);
        const fullUrl = `${IMAGE_URL}${userDetails.profile_data.profile_image}`;
        convertImageUrlToBase64(fullUrl).then((base64) => {
          setInitialValues((prev) => ({
            ...prev,
            profile_image: base64,
          }));
          setLoadingImage(false);
        });
      }
    }
  }, [userDetails]);

  const convertImageUrlToBase64 = async (imageUrl) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required("Required"),
    last_name: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    mobile_number: Yup.string().required("Required"),
  });

  const handleImageToBase64 = (file, setFieldValue) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setFieldValue("profile_image", reader.result);
    };
    if (file) reader.readAsDataURL(file);
  };

  const handleSubmit = (values) => {
    onSave(values);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, handleChange, setFieldValue, touched, errors }) => (
        <Form>
          <Grid container spacing={2} sx={{ ml: 0}}>
            {/* Profile Image */}
            <Grid item xs={12} display="flex" alignItems="center" gap={2}>
  <div
    style={{
      width: 100,
      height: 100,
      borderRadius: "50%",
      color: "#fff",
      fontSize: 28,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      overflow: "hidden",
    }}
  >
    {values.profile_image ? (
       <Avatar
                src={values.profile_image || ""}
                sx={{ width: 100, height: 100 }}
              >
                {!values.profile_image && `${values.first_name?.[0] || ""}${values.last_name?.[0] || ""}`}
              </Avatar>
    ) : (
      // Initials fallback
      `${values.first_name?.[0] || ""}${values.last_name?.[0] || ""}`
    )}
  </div>

  <Button
    variant="outlined"
    component="label"
    startIcon={<UploadIcon />}
  >
    Upload Image
    <input
      type="file"
      hidden
      accept="image/*"
      onChange={(e) =>
        handleImageToBase64(e.target.files[0], setFieldValue)
      }
    />
  </Button>
</Grid>

            {/* Basic Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={values.first_name}
                onChange={handleChange}
                error={touched.first_name && Boolean(errors.first_name)}
                helperText={touched.first_name && errors.first_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={values.last_name}
                onChange={handleChange}
                error={touched.last_name && Boolean(errors.last_name)}
                helperText={touched.last_name && errors.last_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Mobile Number"
                name="mobile_number"
                value={values.mobile_number}
                onChange={handleChange}
              />
            </Grid>

            {/* Gender Dropdown */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={genderOptions}
                getOptionLabel={(option) => option.label}
                value={
                  genderOptions.find((opt) => opt.value === values.gender) || null
                }
                onChange={(e, newValue) => {
                  setFieldValue("gender", newValue ? newValue.value : "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Gender"
                    name="gender"
                    error={touched.gender && Boolean(errors.gender)}
                    helperText={touched.gender && errors.gender}
                  />
                )}
              />
            </Grid>

            {/* Other Personal Fields */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nationality"
                name="nationality"
                value={values.nationality}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="date_of_birth"
                type="date"
                value={values.date_of_birth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Aadhar Number"
                name="aadhar_number"
                value={values.aadhar_number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Residential Address"
                name="residentialAddress"
                value={values.residentialAddress}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Current Address"
                name="currentAddress"
                value={values.currentAddress}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="About"
                name="about"
                value={values.about}
                onChange={handleChange}
              />
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="contained" type="submit">
                Save
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default BasicDetailEditForm;
